import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Tooltip } from "react-tooltip";
import { CheckCircleIcon, NoSymbolIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../../app/hooks";
import { getParamAsInt } from "../../../app/utils";
import { isAdminRole } from "../../../app/userSlice";
import { localeDate } from "../../../app/datetimeUtils";
import Select from "../../../components/input/Select";
import { Loading } from "../../../components/Loading";
import { LoadingTextSkeleton } from "../../../components/LoadingTextSkeleton";
import { CourseStatus } from "../../courses/coursesApiSlice";
import { useViewDraftsForUserQuery } from "./draftAutosaveHistoryApiSlice";
import AutosaveHistoryEssayTable from "./AutosaveHistoryEssayTable";

export default function ViewDraftAutosaveHistory() {
	const { role } = useAppSelector((state) => state.user);
	const navigate = useNavigate();
	const getQueryParams = () => {
		const [queryParams] = useSearchParams();
		const writerId = getParamAsInt(queryParams, "writerId");
		const courseId = getParamAsInt(queryParams, "courseId");

		if (!writerId) {
			navigate(`/writing`);
		}

		return {
			writerId: writerId!,
			courseId: courseId!,
		};
	};
	const { writerId, courseId } = getQueryParams();

	const viewDraftsForUser = useViewDraftsForUserQuery({
		writerId,
		courseId,
	});

	const userDraftHistory = viewDraftsForUser.data;

	// Filtering
	const [selectedPrompt, setSelectedPrompt] = useState<string>();
	const [selectedStatus, setSelectedStatus] = useState<string>();
	const [selectedCourse, setSelectedCourse] = useState<string>();

	// Get all essays across all the courses that were gathered
	const allEssays = useMemo(
		() => userDraftHistory?.essaysByCourse.flatMap((course) => course.essays) ?? [],
		[userDraftHistory]
	);

	const promptTitleOptions = useMemo(
		() =>
			Array.from(new Set(allEssays.map((essay) => essay.promptTitle).filter(Boolean))).sort(
				(a, b) => a.localeCompare(b)
			),
		[allEssays]
	);

	const statusOptions = useMemo(
		() =>
			Array.from(
				new Set(allEssays.map((essay) => essay.lastDraftStatusDescription).filter(Boolean))
			).sort((a, b) => a.localeCompare(b)),
		[allEssays]
	);

	const courseOptions = useMemo(
		() =>
			userDraftHistory?.essaysByCourse.map((course) => ({
				value: course.courseId.toString(),
				label: course.courseName,
			})) ?? [],
		[userDraftHistory]
	);

	const filteredCourses = useMemo(
		() =>
			userDraftHistory?.essaysByCourse.filter((course) => {
				if (!selectedCourse || selectedCourse === "All") return true;
				return course.courseId.toString() === selectedCourse;
			}) ?? [],
		[userDraftHistory, selectedCourse]
	);

	return (
		<section>
			<h1>Draft Autosave History</h1>
			<p className="mt-4">
				The Draft Autosave History page displays all essays that a student has written with
				all their drafts. You can expand a row in the table to see all drafts that were
				written for that essay. You can click on any draft in the expanded essay's table to
				open a full autosave history for that draft. To explore further, click on an
				autosave entry to view the saved text content from that moment in time.
			</p>
			{viewDraftsForUser.isLoading ? (
				<LoadingTextSkeleton className="w-60! mt-4" />
			) : (
				<p className="mt-4 mb-4 text-lg font-medium leading-6 text-gray-900">
					Student: {userDraftHistory?.formalFullName}
				</p>
			)}
			<Loading loading={viewDraftsForUser.isLoading} className="mt-4!" />
			{userDraftHistory && userDraftHistory.essaysByCourse.length > 0 ? (
				<>
					<div className="flex gap-4 mb-4 flex-wrap">
						{courseOptions.length > 1 && (
							<Select
								value={selectedCourse}
								setValue={(v) => setSelectedCourse(v as string)}
								label="Course"
								simpleListBoxId="courseFilter"
								options={[{ value: "All", label: "All" }, ...courseOptions]}
								simpleListBoxClasses="w-80"
							/>
						)}
						<Select
							value={selectedPrompt}
							setValue={(v) => setSelectedPrompt(v as string)}
							label="Prompt"
							simpleListBoxId="promptFilter"
							options={[
								{ value: "All", label: "All" },
								...promptTitleOptions.map((title) => ({
									value: title,
									label: title,
								})),
							]}
							simpleListBoxClasses="w-80"
						/>
						<Select
							value={selectedStatus}
							setValue={(v) => setSelectedStatus(v as string)}
							label="Last Draft Status"
							simpleListBoxId="statusFilter"
							options={[
								{ value: "All", label: "All" },
								...statusOptions.map((status) => ({
									value: status,
									label: status,
								})),
							]}
							simpleListBoxClasses="w-64"
						/>
					</div>
					{filteredCourses.map((course) => {
						return (
							<div key={course.courseId}>
								{isAdminRole(role) && (
									<div className="flex justify-between gap-x-3 items-end mt-8">
										<span className="flex items-center gap-x-4">
											{course.courseStatus === CourseStatus.Open ? (
												<CheckCircleIcon
													className="size-8 text-green-500"
													data-tooltip-id={`status-${course.courseId}-tooltip`}
												/>
											) : (
												<NoSymbolIcon
													className="size-8 text-red-500"
													data-tooltip-id={`status-${course.courseId}-tooltip`}
												/>
											)}
											<Tooltip
												id={`status-${course.courseId}-tooltip`}
												place="top-start"
												content={`${course.courseStatus}`}
											/>
											<h2>{course.courseName}</h2>
										</span>
										<p className="text-sm text-gray-600 ml-2">
											Created: {localeDate(course.createDate)}
										</p>
									</div>
								)}
								<AutosaveHistoryEssayTable
									essays={course.essays}
									selectedPrompt={selectedPrompt}
									selectedStatus={selectedStatus}
								/>
							</div>
						);
					})}
				</>
			) : (
				<div className="mt-4 px-4 py-8 bg-gray-50 border border-gray-200 rounded-lg">
					<p className="text-gray-700 text-center">
						The student has not written any drafts.
					</p>
				</div>
			)}
		</section>
	);
}
